/**
 * useConsent Hook
 *
 * Manages consent state, cookie, and API communication.
 */

import { useState, useCallback, useEffect } from 'react';
import type { ConsentConfig, ConsentChoices, ConsentData, ConsentAction } from './types';

const COOKIE_NAME = 'evcs_consent';
const COOKIE_DURATION = 365;
const VERSION = '1.0.0';

/**
 * Generate UUID v4
 */
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get config from script tag
 */
function getConfig(): ConsentConfig | null {
  const script = document.getElementById('evcs-consent-config');
  if (!script) return null;
  try {
    return JSON.parse(script.textContent || '');
  } catch {
    return null;
  }
}

/**
 * Read consent from cookie
 */
function readCookie(): ConsentData | null {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === COOKIE_NAME && value) {
      try {
        const decoded = atob(decodeURIComponent(value));
        return JSON.parse(decoded);
      } catch {
        return null;
      }
    }
  }
  return null;
}

/**
 * Write consent to cookie
 */
function writeCookie(data: ConsentData): void {
  const encoded = btoa(JSON.stringify(data));
  const expires = new Date();
  expires.setDate(expires.getDate() + COOKIE_DURATION);

  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(encoded)}; expires=${expires.toUTCString()}; path=/; SameSite=Lax; Secure`;
}

/**
 * Update Google Consent Mode
 */
function updateGCM(choices: ConsentChoices): void {
  if (typeof window.gtag !== 'function') return;

  window.gtag('consent', 'update', {
    analytics_storage: choices.analytics ? 'granted' : 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
}

/**
 * Save consent to database via REST API
 */
async function saveToDatabase(
  consentId: string,
  choices: ConsentChoices,
  action: ConsentAction
): Promise<void> {
  const config = getConfig();
  if (!config) return;

  try {
    await fetch(config.restUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-WP-Nonce': config.restNonce,
      },
      body: JSON.stringify({
        consent_id: consentId,
        choices,
        action,
        page_url: window.location.href,
        page_referer: document.referrer || null,
        language: navigator.language,
      }),
    });
  } catch (error) {
    console.error('Failed to save consent:', error);
  }
}

/**
 * useConsent Hook
 */
export function useConsent() {
  const [config] = useState<ConsentConfig | null>(getConfig);
  const [consent, setConsent] = useState<ConsentData | null>(readCookie);
  const [choices, setChoices] = useState<ConsentChoices>(() => {
    const existing = readCookie();
    if (existing) return existing.s;

    // Default: required services true, optional false
    const defaults: ConsentChoices = {};
    const cfg = getConfig();
    if (cfg) {
      Object.values(cfg.services).forEach((service) => {
        defaults[service.id] = service.required;
      });
    }
    return defaults;
  });

  const hasConsent = consent !== null;

  /**
   * Save consent with given choices
   */
  const save = useCallback(
    async (newChoices: ConsentChoices, action: ConsentAction = 'initial') => {
      const consentId = consent?.id || generateUUID();

      // Build GCM state
      const gcm = {
        analytics_storage: newChoices.analytics ? 'granted' : 'denied',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
      } as const;

      // Create consent data
      const data: ConsentData = {
        v: VERSION,
        id: consentId,
        t: Date.now(),
        s: newChoices,
        gcm,
      };

      // Update state
      setConsent(data);
      setChoices(newChoices);

      // Write cookie
      writeCookie(data);

      // Update GCM
      updateGCM(newChoices);

      // Save to database
      await saveToDatabase(consentId, newChoices, action);
    },
    [consent]
  );

  /**
   * Accept all services
   */
  const acceptAll = useCallback(async () => {
    if (!config) return;

    const allAccepted: ConsentChoices = {};
    Object.values(config.services).forEach((service) => {
      allAccepted[service.id] = true;
    });

    await save(allAccepted, hasConsent ? 'update' : 'initial');
  }, [config, save, hasConsent]);

  /**
   * Reject all optional services
   */
  const rejectAll = useCallback(async () => {
    if (!config) return;

    const allRejected: ConsentChoices = {};
    Object.values(config.services).forEach((service) => {
      allRejected[service.id] = service.required; // Only required stay true
    });

    await save(allRejected, hasConsent ? 'revoke' : 'initial');
  }, [config, save, hasConsent]);

  /**
   * Save custom choices
   */
  const saveCustom = useCallback(async () => {
    await save(choices, hasConsent ? 'update' : 'initial');
  }, [choices, save, hasConsent]);

  /**
   * Toggle a service choice
   */
  const toggleService = useCallback((serviceId: string) => {
    setChoices((prev) => ({
      ...prev,
      [serviceId]: !prev[serviceId],
    }));
  }, []);

  /**
   * Apply consent on load if exists
   */
  useEffect(() => {
    if (consent) {
      updateGCM(consent.s);
    }
  }, []);

  return {
    config,
    consent,
    choices,
    hasConsent,
    acceptAll,
    rejectAll,
    saveCustom,
    toggleService,
    setChoices,
  };
}

// Declare gtag on window
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}
